'use client'

import { FormEvent, useEffect, useRef, useState } from 'react'

import { Button } from '@/app/components/Button'
import { Input, Select } from '@/app/components/Form'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import type { AiDemoSection as AiDemoSectionProps } from '@/sanity.types'

type AiDemoSectionComponentProps = {
  block: AiDemoSectionProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

type Newsletter = {
  slug: string
  name: string
}

type StatusResponse = {
  status: string
  error: boolean
}

const NEWSLETTERS_URL = 'https://ai-demo.paved.com/newsletters'
const GENERATE_URL = 'https://ai-demo.paved.com/generate'
const STATUS_URL = 'https://ai-demo.paved.com/status'
const POLL_INTERVAL = 750

export default function AiDemoSection({ block }: AiDemoSectionComponentProps) {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([])
  const [url, setUrl] = useState('')
  const [selectedNewsletter, setSelectedNewsletter] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [statusText, setStatusText] = useState('')
  const [hasError, setHasError] = useState(false)
  const [iframeContent, setIframeContent] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    fetch(NEWSLETTERS_URL)
      .then((res) => res.json())
      .then((data: Newsletter[]) => setNewsletters(data))
      .catch((err) => console.error('Failed to fetch newsletters:', err))

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  const pollStatus = async (url: string, slug: string) => {
    try {
      const response = await fetch(`${STATUS_URL}?url=${encodeURIComponent(url)}&slug=${slug}`)
      const data: StatusResponse = await response.json()

      if (data.error) {
        setStatusText(`Error: ${data.status}`)
        setHasError(true)
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
        }
      } else {
        setStatusText(data.status)
      }
    } catch {
      // Silently ignore polling errors - the main generation request will handle errors
      // Polling may fail initially if the job hasn't been created yet
    }
  }

  const handleUrlBlur = () => {
    if (url && !url.startsWith('http')) {
      setUrl(`https://${url}`)
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!url || !selectedNewsletter) return

    setIsSubmitting(true)
    setHasError(false)
    setStatusText('')
    setIframeContent('')

    const generateUrl = `${GENERATE_URL}?url=${encodeURIComponent(url)}&slug=${selectedNewsletter}`

    pollingIntervalRef.current = setInterval(() => {
      pollStatus(url, selectedNewsletter)
    }, POLL_INTERVAL)

    try {
      const response = await fetch(generateUrl)
      if (!response.ok) throw new Error('Failed to generate')

      const html = await response.text()
      setIframeContent(`data:text/html;charset=utf-8,${encodeURIComponent(html)}`)
      setShowPreview(true)
      setStatusText('')

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }

      setTimeout(() => {
        setStatusText('')
      }, 100)
    } catch {
      setHasError(true)
      setStatusText('Error fetching result.')
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedNewsletterName = newsletters.find((n) => n.slug === selectedNewsletter)?.name

  const submitButtonData = {
    _type: 'button' as const,
    variant: 'primary' as const,
    size: 'md' as const,
    themeMode: null,
    iconPlacement: 'none' as const,
    icon: null,
    dataId: null,
    link: null,
  }

  const sponsorButtonData = selectedNewsletterName
    ? {
        _type: 'button' as const,
        variant: 'secondary' as const,
        size: 'md' as const,
        themeMode: null,
        iconPlacement: 'none' as const,
        icon: null,
        dataId: null,
        link: {
          _type: 'link' as const,
          linkType: 'external' as const,
          label: `Sponsor ${selectedNewsletterName}`,
          href: `https://www.paved.com/sites/${selectedNewsletter}`,
          external: `https://www.paved.com/sites/${selectedNewsletter}`,
          openInNewTab: true,
          ariaLabel: null,
          mailto: null,
          tel: null,
          anchor: null,
          internalPage: null,
          internalPost: null,
        },
      }
    : null

  return (
    <div className="content-container content-container-wide -mt-xl mb-9xl lg:mb-12xl">
      <div
        className={cn(
          'max-w-[1144px] mx-auto lg:flex lg:flex-row lg:gap-6xl xl:gap-10xl',
          !showPreview && !isSubmitting && 'lg:justify-center',
        )}
      >
        {isSubmitting && !showPreview && (
          <div className="bg-neutral-50 rounded-md h-[538px] my-10xl flex items-center justify-center lg:my-0 lg:flex-1">
            <p className="size-header-h4 text-neutral-950">Loading...</p>
          </div>
        )}

        {showPreview && iframeContent && (
          <div className="my-10xl lg:my-0 lg:flex-1">
            <div className="rounded-md overflow-hidden bg-white">
              <iframe
                src={iframeContent}
                className="w-full h-[538px] border-0"
                title="Newsletter Preview"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        )}

        <div
          className={cn(
            'flex flex-col gap-xl max-w-[672px] mx-auto lg:mx-0 lg:justify-center',
            showPreview || isSubmitting
              ? 'lg:w-[380px] lg:max-w-[380px]'
              : 'lg:w-[860px] lg:max-w-[860px]',
          )}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-xl">
            <div
              className={cn(
                'flex flex-col gap-xl',
                !showPreview && !isSubmitting && 'lg:flex-row lg:gap-3xl lg:items-end',
              )}
            >
              <Input
                type="url"
                label="Landing Page"
                placeholder="Enter a valid URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onBlur={handleUrlBlur}
                required
                disabled={isSubmitting}
                labelClassName="size-header-h6 mb-md"
                variant="small"
              />

              {!showPreview && !isSubmitting && (
                <p className="hidden lg:block size-header-h2 text-white-950 leading-[115%] mb-0 self-center">
                  +
                </p>
              )}

              <Select
                label="Sample newsletter"
                value={selectedNewsletter}
                onValueChange={setSelectedNewsletter}
                items={[
                  { value: '', label: 'Select a newsletter' },
                  ...newsletters.map((newsletter) => ({
                    value: newsletter.slug,
                    label: newsletter.name,
                  })),
                ]}
                required
                disabled={isSubmitting}
                labelClassName="size-header-h6 mb-md"
                variant="small"
              />
            </div>

            <div
              className={cn(
                'flex flex-col gap-lg items-center mt-xl',
                !showPreview && !isSubmitting && 'lg:mt-0',
              )}
            >
              <Button
                className={cn('w-full', !showPreview && !isSubmitting && 'lg:w-auto')}
                button={submitButtonData}
                tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                type="submit"
                disabled={isSubmitting || !url || !selectedNewsletter}
              >
                {isSubmitting ? 'Loading...' : 'Preview ad'}
              </Button>

              {statusText && (
                <div
                  className={cn(
                    'size-body-sm text-center',
                    hasError ? 'text-semantic-error' : 'text-neutral-600',
                  )}
                >
                  {statusText}
                </div>
              )}
            </div>
          </form>

          {showPreview && iframeContent && selectedNewsletterName && sponsorButtonData && (
            <div className="mt-xl text-center w-full max-w-[672px] mx-auto lg:mt-0">
              <Button
                className="w-full"
                button={sponsorButtonData}
                tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
